import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { getCommonWeaknesses } from '@/services/channelApi';
import type { CommonWeaknessesResponse } from '@/types/channel';

/**
 * Fetches the most-common body-part weakness labels for a given
 * (channelSlug, templateId) pair.
 *
 * Both params are optional so callers can derive them lazily (e.g. from a
 * click handler that sets state).  The query is disabled when either is absent.
 *
 * 403 (non-owner) surfaces as an error so the component can render an access-
 * denied message.  404 (endpoint missing in older deploys) is swallowed and
 * returns an empty result to avoid crashing during the rollout window.
 */
export function useCommonWeaknesses(
  slug?: string,
  templateId?: string,
  limit?: number,
) {
  return useQuery<CommonWeaknessesResponse>({
    queryKey: ['common-weaknesses', slug, templateId, limit],
    enabled: Boolean(slug && templateId),
    queryFn: async () => {
      try {
        return await getCommonWeaknesses(slug!, templateId!, limit);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          // Endpoint not yet deployed — return empty result to avoid crash.
          return {
            channel_slug: slug!,
            template_id: templateId!,
            total_evals_scored: 0,
            total_evals_in_scope: 0,
            weaknesses: [],
          };
        }
        throw err;
      }
    },
  });
}
